import { LoadingOutlined, ReloadOutlined, ScanOutlined } from "@ant-design/icons";

import { Button, Modal, Spin, message } from "antd";
import React, { useEffect, useRef, useState } from "react";

import styles from "@/components/MykadModal/MykadModal.module.css";
import { IMAGES } from "@/constant";
import { updatePledgeConsentMetadata } from "@/services/Pledge/PledgeApis";

interface MykadModalProps {
  open: boolean;
  pairId: string;
  mykadFinger: "left" | "right" | null;
  selectedBatches: any[];
  onClose: () => void;
  refreshStatus?: (ws_connection_code?: string, batchList?: any[]) => Promise<void>;
  onScanRequest?: (requestId: string | null) => void;
}

const MykadModal: React.FC<MykadModalProps> = ({
  open,
  pairId,
  mykadFinger,
  selectedBatches,
  onClose,
  refreshStatus,
  onScanRequest,
}) => {
  const [fingerInstruction, setFingerInstruction] = useState<"left" | "right" | null>(null);
  const [saving, setSaving] = useState(false);
  const scanRequestId = useRef<string | null>(null);
  const screenWidth = window.innerWidth;

  const modalStyle = screenWidth < 640 ? "95vw" : screenWidth < 1024 ? "85vw" : "28vw";
  const modalStyleForFingerInstruction =
    screenWidth < 640 ? "95vw" : screenWidth < 1024 ? "85vw" : "35vw";
  const loadingSize = screenWidth < 640 ? 75 : screenWidth < 1024 ? 65 : 24;

  const onSelectFinger = async (finger: "left" | "right") => {
    setSaving(true);
    try {
      setFingerInstruction(finger);
      const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      onScanRequest?.(requestId);
      const payload = {
        meta_data: JSON.stringify({
          mykad_finger: finger,
          scan_request_id: requestId,
        }),
        pledge_ids: selectedBatches.flatMap((batch) => batch.pledges).map((pledge) => pledge.id),
      };
      await updatePledgeConsentMetadata(pairId, payload);
      scanRequestId.current = requestId;
    } catch (error) {
      console.error(error);
      onScanRequest?.(null);
      message.error("Unable to request the fingerprint scan. Please try again.");
      setFingerInstruction(null);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (saving) return;
    if (!scanRequestId.current) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updatePledgeConsentMetadata(pairId, {
        meta_data: JSON.stringify({
          mykad_finger: null,
          scan_request_id: scanRequestId.current,
          scan_cancelled: true,
        }),
        pledge_ids: selectedBatches.flatMap((batch) => batch.pledges).map((pledge) => pledge.id),
      });
      message.info(
        "Scan cancellation requested. The scanner may need to finish before another scan can start.",
      );
      onClose();
    } catch (error) {
      console.error(error);
      message.error("Unable to cancel the scan. Please try closing again.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFingerInstruction(mykadFinger);
      scanRequestId.current = null;
    }
  }, [open, mykadFinger]);

  return (
    <Modal
      open={open}
      onCancel={() => void handleClose()}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={null}
      centered
      width={fingerInstruction ? modalStyleForFingerInstruction : modalStyle}
    >
      {fingerInstruction ? (
        <>
          <div className={styles.modalContent}>
            <div className={styles.iconWrapper}>
              <ScanOutlined className={styles.scanOutlined} />
            </div>
            <Spin indicator={<LoadingOutlined style={{ fontSize: loadingSize }} spin />} />
            <div className={styles.modalText}>
              Please scan {fingerInstruction} mother thumb for MyKad Sign.
            </div>
            <Button
              className={styles.connectButton}
              onClick={() => void refreshStatus?.()}
              disabled={saving}
            >
              <ReloadOutlined />
              Refresh to check for MyKad Fingerprint Verification
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.modalContent}>
          <div className={styles.modalTextForFinger}>Kindly choose a finger to scan</div>
          <img className={styles.scanImage} src={IMAGES.FINGER_SCAN} alt="finger scan" />

          <div className={styles.fingerButtonContainer}>
            <Button className={styles.primaryButton} onClick={() => onSelectFinger("left")}>
              Left Mother Thumb
            </Button>
            <Button className={styles.primaryButton} onClick={() => onSelectFinger("right")}>
              Right Mother Thumb
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MykadModal;
